import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Lock, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useSession } from "@/context/SessionContext";
import { useFetch } from "@/lib/useFetch";
import { errMsg, fmtDateTime } from "@/lib/format";
import { EmptyState, ErrorState, LoadingRows, Badge } from "@/components/common/States";

const label = (t) => t.replace(/_/g, " ");

export const FilesPanel = ({ cfg, record }) => {
  const { api, can } = useSession();
  const params = { entity_type: cfg.entity, entity_id: record.id };
  const { data, loading, error, reload } = useFetch("/files", params);
  const types = cfg.docTypes || ["other"];
  const [docType, setDocType] = useState(types[0]);
  const [busy, setBusy] = useState(false);
  const [del, setDel] = useState(null);
  const input = useRef(null);
  const canManage = can(`${cfg.perm}.manage`);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("File must be 10 MB or smaller");
    const fd = new FormData();
    fd.append("file", file); fd.append("entity_type", cfg.entity); fd.append("entity_id", record.id); fd.append("doc_type", docType);
    setBusy(true);
    try { await api.post("/files", fd); toast.success("Document uploaded"); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); input.current.value = ""; }
  };
  const open = async (f) => {
    try {
      const r = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(e.response?.status === 403 ? "You don't have access to this document" : "Could not open document"); }
  };
  const remove = async () => {
    try { await api.delete(`/files/${del.id}`); toast.success("Document removed"); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setDel(null); }
  };

  return (
    <div className="g39-card" data-testid="files-panel">
      {canManage && (
        <div className="flex flex-col sm:flex-row gap-3 p-4 border-b bg-secondary/40">
          <Select value={docType} onValueChange={setDocType}>
            <SelectTrigger className="sm:w-52 bg-white" data-testid="file-doc-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{label(t)}</SelectItem>)}</SelectContent>
          </Select>
          <input ref={input} type="file" accept=".pdf,image/jpeg,image/png,image/webp,image/heic" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="file-input" />
          <Button variant="outline" disabled={busy} onClick={() => input.current.click()} data-testid="file-upload-button"><Upload className="h-4 w-4 mr-1.5" />{busy ? "Uploading…" : "Upload document"}</Button>
          <span className="text-xs text-muted-foreground self-center">PDF or image, up to 10 MB</span>
        </div>
      )}
      {loading && <LoadingRows rows={2} />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && !data.length && <EmptyState title="No documents" body="Registration, insurance, warranty papers and photos appear here." testId="files-empty-state" />}
      <div className="divide-y">
        {(data || []).map((f) => (
          <div key={f.id} className="flex items-center gap-3 px-4 py-3 text-sm" data-testid={`file-row-${f.id}`}>
            {f.sensitive ? <Lock className="h-4 w-4 text-gold" /> : <FileText className="h-4 w-4 text-muted-foreground" />}
            <div className="flex-1 min-w-0">
              <div className="truncate font-medium">{f.filename}</div>
              <div className="text-[11px] text-muted-foreground">{label(f.doc_type)} · {(f.size / 1024).toFixed(0)} KB · {f.uploaded_by_name} · {fmtDateTime(f.created_at)}</div>
            </div>
            {f.sensitive && <Badge tone="gold">Restricted</Badge>}
            <Button size="sm" variant="outline" disabled={!f.can_open} onClick={() => open(f)} data-testid={`file-open-${f.id}`}>Open</Button>
            {canManage && f.can_open && <Button size="icon" variant="ghost" onClick={() => setDel(f)} data-testid={`file-delete-${f.id}`}><Trash2 className="h-4 w-4" /></Button>}
          </div>
        ))}
      </div>
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Remove document?</AlertDialogTitle><AlertDialogDescription>{del?.filename} will be hidden from this record. The removal is audited.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={remove} data-testid="file-delete-confirm">Remove</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
